import {
  AnalyticsService,
  AuthorizationService,
  ExpirationTimePeriod,
  ExportService,
  ExportType,
  LoggingService,
  OSType,
  TimePeriod,
  TimePeriodUtils,
  ToastrService,
  UserPermissions,
} from "app/shared";
import {
  BaseChartSettings,
  UserAnalyticConfiguration,
} from "app/shared/model/charts/user-analytic-configurations.model";
import {
  ChartData,
  ChartOptions,
  ChartType,
  ChartTypeRegistry,
  Plugin,
} from "chart.js";
import { AnyObject } from "chart.js/dist/types/basic";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { CustomCanvasBackgroundColor } from "./plugins/chartBackgroundColorPlugin";
import autocolors from "chartjs-plugin-autocolors";
import { LabelOptions } from "chartjs-plugin-datalabels/types/options";
import { ColorSchemes } from "./plugins/colorschemes/colorschemes";
import { isDate } from "app/shared/utils/date-utils";
import { CommonAnalyticsLogic } from "./CommonAnalyticsLogic";
import { EventEmitter, NgZone } from "@angular/core";

export abstract class BaseChartLogic<
  TConfig,
  TData,
> extends CommonAnalyticsLogic<TConfig> {
  protected exportService: ExportService;
  protected toastrService: ToastrService;

  private getConfigTimeoutId: any;
  private pendingUserAnalyticsConfigurationChanges: UserAnalyticConfiguration[] =
    [];

  //properties that need to be initialized in constructor of extended class
  userConfigurationsList: UserAnalyticConfiguration[] | undefined;
  generalSettings: BaseChartSettings | null = null;
  startDate: Date | null = null;
  endDate: Date | null = null;

  chartPlugins: Plugin<keyof ChartTypeRegistry, AnyObject>[] = [
    ChartDataLabels,
    CustomCanvasBackgroundColor,
    autocolors,
  ];
  chartType: ChartType;
  chartOptions: ChartOptions;
  defaultLabelSet: Record<string, LabelOptions | null> = {
    value: {
      formatter: (value: any, context: any) => {
        return context.chart.data.datasets[0].data[context.dataIndex];
      },
      //display: true,
    },
  };
  labelSet: Record<string, LabelOptions | null> = this.defaultLabelSet;
  chartData: ChartData<ChartType, any, any> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: "",
        datalabels: {
          labels: this.labelSet,
        },
      },
    ],
  };
  listDisplayData: any[] = [];

  //TODO: add handling for groupBy options, for example documents can be grouped by division or language
  groupBy: string[] = [];
  groupByOptions: string[] = [];

  /**
   * Assign type for data object received from analytics.service for chart data
   */
  analyticsData: TData | any | null = null;

  protected chartCanvas: any;

  constructor(
    _analyticsService: AnalyticsService,
    _authService: AuthorizationService,
    _exportService: ExportService,
    _toastrService: ToastrService,
    _loggingService: LoggingService,
    configTypeConstructor: new () => TConfig,
    private _ngZone: NgZone,
  ) {
    super(
      _analyticsService,
      _authService,
      _loggingService,
      configTypeConstructor,
    );

    this.toastrService = _toastrService;
    this.exportService = _exportService;

    this.subscriptions.push(
      _analyticsService.refreshData.subscribe((contactServer: boolean) => {
        _loggingService.log(
          "BaseChartLogic - event - refreshData: ",
          contactServer,
        );
        this.refreshDataShared();
      }),
    );

    this.subscriptions.push(
      _analyticsService.userAnalyticsConfigurationsDataChange.subscribe(
        (data: UserAnalyticConfiguration[]) => {
          this.loggingService.log(
            "BaseChartLogic - event - userAnalyticsConfigurationsDataChange: ",
            data,
          );
          this.handleConfigurationEvent(data);
          this.buildListDisplayData(
            this.analyticsData,
            this.config?.generalSettings?.showAllData || false,
          );
        },
      ),
    );

    this.subscriptions.push(
      _analyticsService.userAnalyticsConfigurationChartDisplayOptionsChange.subscribe(
        (data: UserAnalyticConfiguration) => {
          this.loggingService.log(
            "BaseChartLogic - event - userAnalyticsConfigurationChartDisplayOptionsChange: ",
            data,
          );

          if (this.isLoading) {
            this.pendingUserAnalyticsConfigurationChanges.push(data);
            return;
          }

          this.applyUserAnalyticsConfig(data);
        },
      ),
    );

    if (this.exportService) {
      this.subscriptions.push(
        _exportService.exportButtonClickEvent.subscribe(
          (data: { chart: string; type: ExportType }) => {
            if (data.chart === this.chartDetails.title) {
              this.handleExport(data.type);
            }
          },
        ),
      );

      this.subscriptions.push(
        _exportService.exportAllButtonClickEvent.subscribe(
          (data: { charts: string[]; type: ExportType }) => {
            if (data.charts.includes(this.chartDetails.title)) {
              this.handleExport(data.type, true);
            }
          },
        ),
      );
    }
  }

  protected testForNoData(data: any) {
    if (!data || !data.DataPoints || data.DataPoints.length === 0) {
      return true;
    }
    return false;
  }

  private applyUserAnalyticsConfig(data: UserAnalyticConfiguration) {
    this.loggingService.log(
      "BaseChartLogic - event - userAnalyticsConfigurationChartDisplayOptionsChange: ",
      data,
    );
    this.updateConfig([data], true);
    this.buildListDisplayData(
      this.analyticsData,
      this.config?.generalSettings?.showAllData || false,
    );
  }

  /**
   * Pass en event, and type of data to subscribe to
   * @param eventEmitter
   */
  protected handleDataSubscription(eventEmitter: EventEmitter<TData>): void {
    this.subscriptions.push(
      eventEmitter.subscribe((data: TData) => {
        this._ngZone.run(() => {
          if (this.testForNoData(data)) {
            this.handleNoData();
            return;
          }
          this.processDataForChart(data);
        });
      }),
    );
  }

  private addUniqueKeyToRow(
    row: Record<string, any>,
    baseKey: string,
    value: any,
  ): void {
    let uniqueKey = baseKey;
    let suffix = 1;
    while (this.config?.generalSettings?.legendLabels.includes(uniqueKey)) {
      uniqueKey = `${baseKey}_${suffix}`;
      suffix++;
    }
    row[uniqueKey] = value;
  }

  private isDictionary(value: any): boolean {
    // Check if the object is a dictionary-like structure
    return (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).every((key) => typeof key === "string")
    );
  }

  private flattenObject(
    obj: any,
    parentKey: string = "",
    result: Record<string, any> = {},
    addTotalColumn: boolean = true,
  ): Record<string, any> {
    const _exclude = this.chartDetails?.excludeColumns || [];
    for (const key in obj) {
      if (!obj.hasOwnProperty(key)) continue;
      const fullPath = parentKey ? `${parentKey}.${key}` : key;
      if (_exclude.includes(fullPath)) continue;
      const value = obj[key];

      // Special handling for action columns - preserve the object structure
      if (
        key.startsWith("__action_") &&
        value &&
        typeof value === "object" &&
        value.actionComponent
      ) {
        result[key] = value; // Keep the action object intact
        continue;
      }

      if (Array.isArray(value)) {
        // Handle arrays by appending their content
        value.forEach((item, index) => {
          if (item && typeof item === "object") {
            // Recurse for objects in the array
            this.flattenObject(
              item,
              `${fullPath}[${index}]`,
              result,
              addTotalColumn,
            );
          } else {
            // Add primitive values from the array
            result[`${fullPath}[${index}]`] = item;
          }
        });
      } else if (value && typeof value === "object") {
        if (this.isDictionary(value)) {
          // Handle dictionary-like objects
          for (const dictKey in value) {
            if (_exclude.includes(`${fullPath}.${dictKey}`)) continue;
            result[`${fullPath}.${dictKey}`] = value[dictKey];
          }
        } else {
          // Recurse for nested objects
          this.flattenObject(value, fullPath, result, addTotalColumn);
        }
      } else {
        // Add the value to the result with its full path as the key
        result[fullPath] = value;
      }
    }

    if (addTotalColumn) {
      this.addUniqueKeyToRow(
        result,
        this.dataPointKey ?? "value",
        this.getDataPointDisplayValue(obj),
      );
    }

    return result;
  }

  private formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  protected updateChartTitle(): string {
    let title = this.chartDetails?.title || "Chart";

    if (isDate(this.startDate) && isDate(this.endDate)) {
      const formattedStart = this.formatDate(new Date(this.startDate));
      const formattedEnd = this.formatDate(new Date(this.endDate));
      title += ` (${formattedStart} - ${formattedEnd})`;
    } else if (this.config?.period) {
      const period = this.config.period as TimePeriod;
      const calculatedStartDate = this.calculateStartDateFromPeriod(period);
      const formattedStart = this.formatDate(calculatedStartDate);
      const formattedEnd = this.formatDate(new Date());
      title += ` (${period}: ${formattedStart} - ${formattedEnd})`;
    } else {
      title += "";
    }

    return title;
  }

  private calculateStartDateFromPeriod(period: TimePeriod): Date {
    const endDate = new Date();
    return TimePeriodUtils.subtractTimePeriodFromDate(period, endDate);
  }

  private formatTimePeriod(period: TimePeriod): string {
    // Customize this to format the time period name if needed
    return TimePeriodUtils.getStringForTimePeriod(period);
  }

  private handlePDFExport(
    title: string,
    shortDescription: string,
    longDescription: string,
    isExportingAll: Boolean = false,
  ): void {
    const originalChart = this.chartCanvas?.nativeElement;

    if (!originalChart) {
      console.error("Could not find chart canvas element.");
      this.toastrService.displayToastrMessage(
        "error",
        "Chart not found for PDF export.",
      );
      this.exportService.exportComplete();
      return;
    }
    if (isExportingAll) {
      this.exportService.storeDataForExportAll(
        originalChart,
        title,
        shortDescription,
        longDescription,
      );
    } else {
      this.exportService.exportToPDF(
        originalChart,
        title,
        shortDescription,
        longDescription,
      );
    }
  }

  private handlePNGExport(
    title: string,
    shortDescription: string,
    longDescription: string,
  ): void {
    const originalChart = this.chartCanvas?.nativeElement;

    if (!originalChart) {
      console.error("Could not find chart canvas element.");
      this.toastrService.displayToastrMessage(
        "error",
        "Chart not found for PNG export.",
      );
      this.exportService.exportComplete();
      return;
    }

    this.exportService.exportToPNG(
      originalChart,
      title,
      shortDescription,
      longDescription,
    );
  }

  private getDataForExport(allData: boolean): any[] {
    const data = this.buildListDisplayData(this.analyticsData, allData, false);

    if (data.length === 0) return [];

    const flattenedData: any[] = [];
    const keys = Object.keys(data[0]);
    const secondaryKeys = keys.filter((key) => key.startsWith("__secondary_"));

    if (secondaryKeys.length === 0) {
      return data;
    }

    const columnCount = keys.length - secondaryKeys.length;

    data.forEach((row) => {
      //push original row
      const rowClone = { ...row };
      secondaryKeys.forEach((key) => {
        delete rowClone[key];
      });
      flattenedData.push(rowClone);

      //clear original row to display secondary data
      const rowClone2 = { ...rowClone };
      keys.forEach((key) => {
        if (key in rowClone2) rowClone2[key] = null;
      });

      secondaryKeys.forEach((key) => {
        const secondaryRows = row[key] as Record<string, any>[];
        secondaryRows.forEach((secondaryRow) => {
          const newRow = { ...rowClone2, ...secondaryRow };

          // secondaryKeys.forEach((secondaryKey) => {
          //   if (secondaryKey !== key) {
          //     newRow[secondaryKey] = null;
          //   }
          // });
          flattenedData.push(newRow);
        });
      });
    });

    return flattenedData;
  }

  protected handleExport(
    type: ExportType,
    isExportingAll: Boolean = false,
  ): void {
    const title = this.updateChartTitle();
    const shortDescription = this.chartDetails?.shortDescription ?? "";
    const longDescription = this.chartDetails?.fullDescription ?? "";

    this.loggingService.log(`handleExport of ${title}: `, type);

    switch (type) {
      case ExportType.CSV:
        this.exportService.exportToCSV(
          this.getDataForExport(false),
          title,
          shortDescription,
          longDescription,
        );
        break;
      case ExportType.Excel:
        if (!isExportingAll) {
          this.exportService.exportToExcel(
            this.getDataForExport(false),
            title,
            shortDescription,
            longDescription,
          );
        } else {
          this.exportService.storeDataForExportAll(
            this.getDataForExport(false),
            title,
            shortDescription,
            longDescription,
          );
        }
        break;
      case ExportType.CSVRAW:
        this.exportService.exportToCSV(
          this.buildListDisplayData(this.analyticsData, true, false),
          title,
          shortDescription,
          longDescription,
        );
        break;
      case ExportType.ExcelRAW:
        this.exportService.exportToExcel(
          this.buildListDisplayData(this.analyticsData, true, false),
          title,
          shortDescription,
          longDescription,
        );
        break;
      case ExportType.PDF:
        this.handlePDFExport(
          title,
          shortDescription,
          longDescription,
          isExportingAll,
        );
        break;
      case ExportType.PNG:
        this.handlePNGExport(title, shortDescription, longDescription);
        break;
    }
  }

  private handleConfigurationEvent(data: UserAnalyticConfiguration[]): void {
    this.loggingService.log(
      "BaseChartLogic - handleConfigurationEvent: ",
      data,
    );
    clearTimeout(this.getConfigTimeoutId);
    if (data && Array.isArray(data)) {
      this.updateConfigShared(data);
    } else {
      this.initializeConfig();
    }
  }

  /**
   * If the chart initialized after settings are already emitted, this method is intended
   * to ensure that they will load.
   */
  private checkForUserConfig(): void {
    this.loggingService.log("BaseChartLogic - checkForUserConfig");
    if (this.config) {
      clearTimeout(this.getConfigTimeoutId);
      return;
    }

    if (this.analyticsService.userAnalyticsConfigurationsData) {
      clearTimeout(this.getConfigTimeoutId);
      this.handleConfigurationEvent([
        ...this.analyticsService.userAnalyticsConfigurationsData,
      ]);
      return;
    }

    this.analyticsService.getUserAnalyticsConfigurationsData(
      this.authService.userId,
    );
  }

  private updateConfigShared(data: UserAnalyticConfiguration[]): void {
    this.loggingService.log("BaseChartLogic - updateConfigShared: ", data);
    this.userConfigurationsList = data;
    this.updateConfig(data, false);
  }

  /**
   * Receive data from analytics and translate to data used in chart
   * @param data
   */
  protected processDataForChart(data?: TData) {
    this.analyticsData = data;
    this.loggingService.log("BaseChartLogic - processDataForChart");
    //clear values
    this.chartData.labels = [];
    this.listDisplayData = [];
    this.chartData.datasets[0].data = [];
    this.chartData.datasets[0].label = this.chartDetails.title || "";
    this.chartOptions.plugins.title.text = this.chartDetails.title || "";

    if (
      !this.analyticsData ||
      !this.analyticsData.DataPoints ||
      this.analyticsData.DataPoints.length === 0
    ) {
      return;
    }

    //add common data back
    this.chartData.datasets[0].label = this.chartDetails?.title || "";
    this.buildListDisplayData(
      this.analyticsData,
      this.config?.generalSettings?.showAllData || false,
    );
    this.chartData.datasets[0].data = this.analyticsData.DataPoints.map((dp) =>
      this.getDataPointDisplayValue(dp),
    );
    this.handleLabels(this.analyticsData);
    this.handleChartDisplaySettings();
    this.hasData = true;
    this.loggingService.log(
      "BaseChartLogic - processDataForChart - set isLoading = false",
    );

    this.isLoading = false;
    for (const config of this.pendingUserAnalyticsConfigurationChanges) {
      this.applyUserAnalyticsConfig(config);
    }
    this.pendingUserAnalyticsConfigurationChanges = [];
  }

  protected resolveDeepPath(obj: any, path: string): any {
    const keys = path.split(".");
    let value = obj;

    for (const key of keys) {
      if (value && typeof value === "object" && key in value) {
        value = value[key];
      } else {
        return undefined; // Path does not exist
      }
    }
    return value;
  }

  /**
   *
   */
  protected abstract dataPointKey: string;

  /**
   * Method to return primary value for display in chart and list
   */
  protected abstract getDataPointDisplayValue(dataPoint: any): void;

  /**
   * This method must be redefined in the child class to return a custom label for a given data point
   * All handling is 100% custom
   * @param dataPoint
   * @param label
   * @returns
   */
  protected getCustomLabel(dataPoint: any, label: string): any {
    this.loggingService.warn(
      "BaseChartLogic - getCustomLabel called, but not implemented correctly: ",
      dataPoint,
      label,
    );
    return "";
  }

  // Helper to check if a value is a "default" date (e.g., 1970-01-01 or 0001-01-01)
  private isDefaultDate = (value: any): boolean => {
    if (!value) return true;

    // Check for known default date strings (ISO or partial)
    if (typeof value === "string") {
      // Match 0001-01-01 or 1970-01-01 at the start of the string
      if (
        /^0001-01-01([T\s]|$)/.test(value) ||
        /^1970-01-01([T\s]|$)/.test(value)
      ) {
        return true;
      }
    }

    // Accept both Date objects and ISO strings
    let date: Date | null = null;
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === "string") {
      // Try to parse as date
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        date = parsed;
      }
    }
    if (!date) return false;
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    // 1970-01-01 or 0001-01-01
    return (
      (year === 1970 && month === 1 && day === 1) ||
      (year === 1 && month === 1 && day === 1)
    );
  };

  private removeEmptyColumns(data: any[]): any[] {
    if (data.length === 0) return data;

    const allKeys = Object.keys(data[0]);

    const nonEmptyKeys = allKeys.filter((key) => {
      return data.some((row) => {
        const value = row[key];
        // Exclude null, undefined, or empty string
        if (value === null || value === undefined || value === "") return false;
        // Exclude default dates
        if (
          typeof value === "string" &&
          /^\d{4}-\d{2}-\d{2}/.test(value) &&
          this.isDefaultDate(value)
        ) {
          return false;
        }
        if (value instanceof Date && this.isDefaultDate(value)) {
          return false;
        }
        return true;
      });
    });

    return data.map((row) => {
      const filteredRow: Record<string, any> = {};
      nonEmptyKeys.forEach((key) => {
        let value = row[key];
        // Remove bad date values from non-empty rows
        if (
          (typeof value === "string" &&
            /^\d{4}-\d{2}-\d{2}/.test(value) &&
            this.isDefaultDate(value)) ||
          (value instanceof Date && this.isDefaultDate(value))
        ) {
          value = null;
        }
        filteredRow[key] = value;
      });
      return filteredRow;
    });
  }

  /**
   * Build the list data for display in the basic list view
   * @param data
   */
  protected buildListDisplayData(
    data: any,
    allData: boolean = false,
    updatePage: boolean = true,
  ): any[] {
    this.loggingService.log(
      "BaseChartLogic - buildListDisplayData - raw data: ",
      data,
    );
    if (!Array.isArray(data?.DataPoints)) {
      console.warn("DataPoints is not a valid array.");
      return [];
    }
    if (allData) {
      let listData = data.DataPoints.map((dp: any) =>
        this.flattenObject(
          dp,
          "",
          {},
          this.chartDetails?.hideTotalColumn === true ? false : true,
        ),
      );

      // Remove columns with no data
      listData = this.removeEmptyColumns(listData);

      if (updatePage) {
        this.listDisplayData = listData;
      }

      this.loggingService.log(
        "BaseChartLogic - buildListDisplayData - allData - result: ",
        listData,
      );
      return listData;
    } else {
      const listData = data.DataPoints.map((dp: any) => {
        const row: Record<string, any> = {};

        //get labels/columns from settings, or default
        const labels =
          Array.isArray(this.config?.generalSettings?.legendLabels) &&
          this.config?.generalSettings?.legendLabels.length > 0
            ? this.config.generalSettings.legendLabels
            : this.chartDetails.defaultLabels;

        //for each column, resolve the value (determine if it allows simple data mapping or requires custom handling)
        labels.forEach((label: string) => {
          const [_label, value] = this.resolveLabel(label, dp);
          row[_label] = value;
        });

        // Add primary data value for display in list, such as Total
        if (this.chartDetails?.hideTotalColumn !== true) {
          this.addUniqueKeyToRow(
            row,
            this.dataPointKey ?? "value",
            this.getDataPointDisplayValue(dp),
          );
        }

        return row;
      });

      // Clean up potential conflicts of secondary columns, if no data, they would be simple string, null, or undefined
      listData.forEach((row) => {
        // Clean up mismatched secondary columns
        this.secondaryColumnMap.forEach((secondaryColumnName, primaryKey) => {
          if (row.hasOwnProperty(primaryKey)) {
            // Remove the mismatched primary key
            delete row[primaryKey];
            // Add the secondary column name with an empty array value
            row[secondaryColumnName] = [];
          }
        });

        // Ensure every row contains all secondary columns
        this.secondaryColumnMap.forEach((secondaryColumnName) => {
          if (!row.hasOwnProperty(secondaryColumnName)) {
            row[secondaryColumnName] = [];
          }
        });
      });

      if (updatePage) {
        this.listDisplayData = listData;
      }

      this.loggingService.log(
        "BaseChartLogic - buildListDisplayData - selectData - result: ",
        listData,
      );
      return listData;
    }
  }

  private formatSecondaryValue(value: any): any {
    return Array.isArray(value)
      ? value.map((item) => this.flattenObject(item, "", {}, false))
      : [this.flattenObject(value, "", {}, false)];
  }

  private secondaryColumnMap: Map<string, string> = new Map();
  private addSecondaryColumn(dp: any, path: string, value: any): [string, any] {
    // Check if the path already maps to a secondary column
    if (this.secondaryColumnMap.has(path)) {
      const existingColumn = this.secondaryColumnMap.get(path)!;
      return [existingColumn, this.formatSecondaryValue(value)];
    }

    // Else,Generate a new unique secondary column name
    // const secondaryColumnName = `__secondary_${
    //   this.secondaryColumnMap.size + 1
    // }`;
    const secondaryColumnName = `__secondary_${path}`;

    this.secondaryColumnMap.set(path, secondaryColumnName);

    return [secondaryColumnName, this.formatSecondaryValue(value)];
  }

  private resolveLabel(path: string, dp: any): [string, any] {
    const isCustomLabels =
      this.chartDetails?.customLabels && this.chartDetails.customLabels[path];

    var value = isCustomLabels
      ? this.getCustomLabel(dp, path)
      : this.resolveDeepPath(dp, path);

    if (Array.isArray(value)) {
      const isSimpleArray = value.every((item) =>
        ["string", "number", "boolean"].includes(typeof item),
      );
      if (isSimpleArray) {
        // Return joined string for simple arrays
        value = value.join("; ");
      } else {
        return this.addSecondaryColumn(dp, path, value);
      }
    } else if (value instanceof Date) {
      // Format Date objects as readable strings
      value = value.toLocaleString();
    } else if (value && typeof value === "object") {
      return this.addSecondaryColumn(dp, path, value);
    }

    return [path, value !== undefined ? value : `Unavailable: ${path}`];
  }

  private mapSettingsToLegendLabels(data): void {
    // this.loggingService.log(
    //   "BaseChartLogic - mapSettingsToLegendLabels: ",
    //   data
    // );

    // this.loggingService.log(
    //   "BaseChartLogic - mapSettingsToLegendLabels - build labels - from settings: ",
    //   this.config?.generalSettings?.legendLabels
    // );

    const legendLabels = this.config?.generalSettings?.legendLabels;

    if (Array.isArray(legendLabels) && legendLabels.length > 0) {
      this.chartData.labels = data.DataPoints.map((dp: any) => {
        return this.config.generalSettings.legendLabels.map((path: string) => {
          return this.resolveLabel(path, dp)[1];
        });
      });
      return;
    }

    const defaultLabels = this.chartDetails.defaultLabels;
    if (defaultLabels && defaultLabels.length > 0) {
      console.warn("Legend labels not found, using defaultLabels instead.");
      this.chartData.labels = data.DataPoints.map((dp) => {
        return defaultLabels.map((path) => this.resolveLabel(path, dp)[1]);
      });
    } else {
      console.warn("No legendLabels or defaultLabels configured.");
      this.chartData.labels = data.DataPoints.map(() => [
        "No labels configured",
      ]);
    }
  }

  private mapSettingsToDisplayLabels(): void {
    this.loggingService.log(
      "BaseChartLogic - mapSettingsToDisplayLabels: ",
      this.config?.generalSettings?.displayLabels,
    );

    if (
      !this.config?.generalSettings?.showLabels ||
      !this.config?.generalSettings?.displayLabels ||
      !this.config?.generalSettings?.legendLabels ||
      this.config?.generalSettings?.displayLabels.length === 0
    ) {
      this.labelSet = this.defaultLabelSet;
      this.loggingService.log(
        "BaseChartLogic - mapSettingsToDisplayLabels - set to default",
      );
    }

    let newLabelSet: Record<string, LabelOptions | null> = {};
    const labelIndexes = this.config.generalSettings.displayLabels
      .map((displayLabel) => {
        const index =
          this.config.generalSettings.legendLabels.indexOf(displayLabel);

        if (index === -1) {
          console.warn(
            `Display label "${displayLabel}" not found in legend labels.`,
          );
          return undefined;
        }

        return index;
      })
      .filter((index): index is number => index !== undefined); // Filter out undefined values

    newLabelSet["customLabel"] = {
      //align: 'center',
      formatter: (value: any, context: any) => {
        const datasetLabel = labelIndexes.map(
          (i) => context.chart.data.labels[context.dataIndex][i],
        );
        datasetLabel.push(
          context.chart.data.datasets[0].data[context.dataIndex],
        );
        //this.loggingService.log('adding label: ', datasetLabel)
        return datasetLabel;
      },
      //display: true,
    };

    this.labelSet = { ...newLabelSet };
    this.chartData.datasets[0].datalabels.labels = this.labelSet;
  }

  private handleLabels(data: any): void {
    this.loggingService.log(
      "DownloadedManualsChartComponent - handleLabels data",
      data,
    );
    this.mapSettingsToLegendLabels(data);
    this.mapSettingsToDisplayLabels();
  }

  protected addPeriodtoFilterSettings(): void {
    this.loggingService.log("BaseChartLogic - addPeriodtoFilterSettings");
    this.filterSettings.Period = Object.values(TimePeriod).includes(
      this.config?.period as TimePeriod,
    )
      ? (this.config?.period as TimePeriod)
      : TimePeriod.AllTime;
  }

  // need to do something with this
  protected addExpirationPeriodtoFilterSettings(): void {
    this.loggingService.log(
      "[addExpirationPeriodtoFilterSettings] config.sessionExpirationPeriod:",
      this.config?.sessionExpirationPeriod,
    );
    this.loggingService.log(
      "[addExpirationPeriodtoFilterSettings] this.filterSettings.ExpirationPeriod:",
      this.filterSettings.ExpirationPeriod,
    );
    this.filterSettings.ExpirationPeriod = Object.values(
      ExpirationTimePeriod,
    ).includes(this.config?.sessionExpirationPeriod as ExpirationTimePeriod)
      ? (this.config?.sessionExpirationPeriod as ExpirationTimePeriod)
      : ExpirationTimePeriod.TwentyFourHours;
  }

  protected addStartEndDatesToFilterSettings(
    startDate: Date | string | null | undefined,
    endDate: Date | string | null | undefined,
  ): void {
    this.filterSettings.FilterByStartDate = isDate(startDate)
      ? startDate
      : null;
    this.filterSettings.FilterByEndDate = isDate(endDate) ? endDate : null;
  }

  protected addPermissionsToFilterSettings(): void {
    this.loggingService.log("BaseChartLogic - addPermissionsToFilterSettings");

    if (
      this.config.userPermission == UserPermissions.All ||
      this.config.userPermission == UserPermissions.KomatsuEmployee
    ) {
      this.filterSettings.FilterByAllEmployees = "true";
    }

    this.filterSettings.FilterBySelectedPermissions =
      this.config.userPermission == UserPermissions.All
        ? []
        : [this.config.userPermission as UserPermissions];
  }

  protected addOsTypeToFilterSettings(): void {
    this.loggingService.log("BaseChartLogic - addOsTypeToFilterSettings");

    this.filterSettings.FilterByOsType =
      this.config.osType === OSType.ALL
        ? OSType.ALL
        : (this.config.osType as OSType);
  }

  protected handleNoData(): void {
    this.loggingService.log("BaseChartLogic - handleNoData");
    this.processDataForChart();
    this.hasData = false;
    this.loggingService.log(
      "BaseChartLogic - handleNoData - set isLoading = false",
    );
    this.isLoading = false;
    for (const config of this.pendingUserAnalyticsConfigurationChanges) {
      this.applyUserAnalyticsConfig(config);
    }
    this.pendingUserAnalyticsConfigurationChanges = [];
    this.toastrService.displayToastrMessage(
      "success",
      `No data found for the selected filter in ${this.chartDetails?.title}.`,
    );
  }

  private updateConfig(
    data: UserAnalyticConfiguration[],
    displayOnly: boolean = false,
  ): void {
    this.loggingService.log(
      `BaseChartLogic - updateConfig w/ server ${displayOnly}: `,
      data,
    );
    let config = data.find((c) => c.configType === this.getConfigType());
    if (config) {
      this.assignConfigData(config);
      this.generalSettings = this.config.generalSettings;
      if (displayOnly) {
        this.handleLabels(this.analyticsData);
        this.handleChartDisplaySettings();
      } else {
        this.refreshDataShared();
      }
      return;
    }
    this.initializeConfig();
  }

  /**
   * Initialize the configuration data, specific to the child class, also implements shared logic in the base class
   */
  private initializeConfig(): void {
    this.loggingService.log("BaseChartLogic - initializeConfig");
    if (
      !this.userConfigurationsList ||
      !Array.isArray(this.userConfigurationsList)
    ) {
      this.userConfigurationsList = [];
    }
    this.assignConfigData();
    const config = this.getConfigModel(this.config);
    this.userConfigurationsList.push(config);
    this.refreshDataShared();
  }

  /**
   * Build the filter settings for the chart
   */
  protected abstract buildFilterSettings(): void;

  private refreshDataShared() {
    this.loggingService.log("BaseChartLogic - refreshDataShared");
    this.isLoading = true;
    this.buildFilterSettings();
    this.refreshData();
  }

  /**
   * Refresh the data for the chart
   */
  protected abstract refreshData(): void;

  private getBorderWidth(): number {
    if (!this.generalSettings?.showBorder) return 0;
    return this.generalSettings?.chartType == "pie" ||
      this.generalSettings?.chartType == "doughnut"
      ? 1
      : 0;
  }

  private setColorScheme(): void {
    //default color scheme randomly stopped working

    const defaultColorScheme = BaseChartSettings.defaults.chartJSColorScheme;

    if (!this.generalSettings?.colorScheme) {
      // Clear background color if no scheme is set or if it matches the default
      this.chartData.datasets[0].backgroundColor = null;
      this.loggingService.log(
        "BaseChartLogic - setColorScheme - no color scheme set",
      );
      return;
    }

    const [group, scheme] = this.generalSettings.colorScheme.split(".");

    // Validate group and scheme existence
    if (group in ColorSchemes && scheme in ColorSchemes[group]) {
      const colors = ColorSchemes[group][scheme];

      if (Array.isArray(colors)) {
        // Assign the validated color scheme
        this.chartData.datasets[0].backgroundColor = colors;
      } else {
        console.warn(`Color scheme '${group}.${scheme}' is not a valid array.`);
        this.chartData.datasets[0].backgroundColor = null;
      }
    } else {
      console.error(
        `Color scheme '${group}.${scheme}' not found in ColorSchemes.`,
      );
      this.chartData.datasets[0].backgroundColor = null;
    }
  }

  private handleChartDisplaySettings(): void {
    this.loggingService.log(
      "BaseChartLogic - handleChartDisplaySettings : ",
      this.generalSettings,
    );
    this.chartType = (this.generalSettings?.chartType ||
      "bar") as keyof ChartTypeRegistry;

    const defaultChartOptions: ChartOptions = {
      responsive: this.generalSettings?.responsive === true,
      maintainAspectRatio: false, //this.generalSettings?.responsive === true,
      layout: {
        autoPadding: true,
        padding: {
          left:
            this.generalSettings?.padding?.left ||
            BaseChartSettings.defaults.padding.left,
          right:
            this.generalSettings?.padding?.right ||
            BaseChartSettings.defaults.padding.right,
          top:
            this.generalSettings?.padding?.top ||
            BaseChartSettings.defaults.padding.top,
          bottom:
            this.generalSettings?.padding?.bottom ||
            BaseChartSettings.defaults.padding.bottom,
        },
      },
      elements: {
        arc: {
          borderWidth: this.getBorderWidth(),
        },
      },
      plugins: {
        legend: {
          display: this.generalSettings?.showLegend || false,
          position: this.generalSettings?.legendPosition || "top",
          title: {
            display: this.showTitle || false,
          },
        },
        title: {
          display: this.showTitle || false,
          text: this.updateChartTitle(), //this.chartDetails.title || "",
        },
        datalabels: {
          display: this.generalSettings?.showLabels === true,
          color:
            this.generalSettings?.labelFontColor ||
            BaseChartSettings.defaults.labelFontColor,
          font: {
            weight: "bold",
            size:
              this.generalSettings?.labelFontSize ||
              BaseChartSettings.defaults.labelFontSize,
          },
          anchor: (this.generalSettings?.labelAnchorPosition ||
            BaseChartSettings.defaults.labelAnchorPosition) as
            | "center"
            | "start"
            | "end",
          align: (this.generalSettings?.labelAlignment ||
            BaseChartSettings.defaults.labelAlignment) as
            | "center"
            | "start"
            | "end"
            | "right"
            | "bottom"
            | "left"
            | "top",
          offset:
            this.generalSettings?.labelOffset ||
            BaseChartSettings.defaults.labelOffset,
        },
        customCanvasBackgroundColor: {
          color:
            this.generalSettings?.backgroundColor ||
            BaseChartSettings.defaults.backgroundCanvasColor,
        },
        autocolors: {
          enabled: this.generalSettings?.colorScheme === "extended",
          mode: BaseChartSettings.defaults.extendedColorScheme as
            | "data"
            | "dataset"
            | "label",
        },
        colors: {
          enabled: true,
        },
      },
    };

    this.setColorScheme();

    this.chartOptions = defaultChartOptions;

    if (this.exportService) {
      this.exportService.upsertExportOptions(
        this.chartDetails.title,
        this.generalSettings?.chartType,
      );
    }
  }

  protected init(): void {
    this.isLoading = true;
    if (!this.config) {
      //if settings is not loaded when the chart is initialized, set a timeout to wait for it
      this.getConfigTimeoutId = setTimeout(() => {
        this.checkForUserConfig();
      }, 5000);
    }

    this.handleChartDisplaySettings();
  }

  protected cleanup(): void {
    super.cleanup();

    if (this.exportService) {
      this.exportService.removeExportOptions(this.chartDetails.title);
    }
  }
}
